import { useEffect, useMemo, useRef } from 'react'
import {
  classifyTransactions,
  aggregateRevenueByDimension,
  type RevenueTransaction,
  type ClassifiedTransactions,
} from '@/lib/utils/revenueUtils'
import { reportAttributionFailure } from '@/lib/observability/attribution'

export type RevenueByDimension = ReturnType<typeof aggregateRevenueByDimension>

const emptyDims: RevenueByDimension = {
  bySource: new Map(), byMedium: new Map(), byCampaign: new Map(),
  byContent: new Map(), byTerm: new Map(), byReferrer: new Map(),
  byCountry: new Map(), byLandingPage: new Map(), byOS: new Map(), byBrowser: new Map(),
}

const emptyClassified: ClassifiedTransactions = {
  attributed: [], unattributed: [], attributedCents: 0, unattributedCents: 0, totalCents: 0,
}

interface UseRevenueClassificationResult {
  revenueAll: any[]
  revenueAttribution: RevenueTransaction[]
  classified: ClassifiedTransactions
  revenueDims: RevenueByDimension
  totalRevenueCents: number
  totalRevenueDollars: number
  attributedRevenueDollars: number
  unattributedRevenueDollars: number
}

export function useRevenueClassification(
  dashboardData: any,
  revenueMetrics?: any,
  scope?: { propertyId?: string; period?: string },
): UseRevenueClassificationResult {
  // The classification memos run during render, where reporting to Sentry would
  // be a side effect. Failures are queued and flushed after commit instead.
  const queuedFailures = useRef<unknown[]>([])
  useEffect(() => {
    if (queuedFailures.current.length === 0) return
    const failures = queuedFailures.current
    queuedFailures.current = []
    failures.forEach((error) =>
      reportAttributionFailure('classification', error, { propertyId: scope?.propertyId }, {
        period: scope?.period,
      }),
    )
  })

  const revenueAll = useMemo(() => {
    try {
      return revenueMetrics?.metrics?.trends?.revenueAll
        ?? revenueMetrics?.metrics?.trends?.revenue
        ?? dashboardData?.metrics?.trends?.revenueAll
        ?? dashboardData?.metrics?.trends?.revenue
        ?? []
    } catch { return [] }
  }, [dashboardData, revenueMetrics])

  const revenueAttribution: RevenueTransaction[] = useMemo(() => {
    try {
      // Prefer the dedicated revenue endpoint data over dashboardData fallback
      const data = revenueMetrics?.metrics?.trends?.revenueAttribution ?? dashboardData?.metrics?.trends?.revenueAttribution
      return Array.isArray(data) ? data : []
    } catch { return [] }
  }, [dashboardData, revenueMetrics])

  const classified: ClassifiedTransactions = useMemo(() => {
    try {
      return classifyTransactions(revenueAttribution)
    } catch (err) {
      queuedFailures.current.push(err)
      return emptyClassified
    }
  }, [revenueAttribution])

  const totalRevenueCents = useMemo(() => {
    try {
      return (Array.isArray(revenueAll) ? revenueAll : []).reduce(
        (sum: number, r: any) => sum + (Number(r?.total_revenue) || 0), 0
      )
    } catch { return 0 }
  }, [revenueAll])

  const totalRevenueDollars = totalRevenueCents / 100
  const attributedRevenueDollars = classified.attributedCents / 100
  const unattributedRevenueDollars = Math.max(0, totalRevenueDollars - attributedRevenueDollars)

  const revenueDims = useMemo(() => {
    try {
      return aggregateRevenueByDimension(revenueAttribution)
    } catch (err) {
      queuedFailures.current.push(err)
      return emptyDims
    }
  }, [revenueAttribution])

  return {
    revenueAll,
    revenueAttribution,
    classified,
    revenueDims,
    totalRevenueCents,
    totalRevenueDollars,
    attributedRevenueDollars,
    unattributedRevenueDollars,
  }
}
